import { throwError } from "@/lib/throwError";
import { del, get, post, put } from "@/services/axiosUitils";
import { DynamicPayload } from "@/utils/types/dynamicModule";

const basePath = "/api/formily/dynamic-model";

/** Balance: smaller than 1000 to avoid truncated bodies under load; large enough to limit round-trips. */
const GRID_FETCH_CHUNK = 200;
const GRID_FETCH_MAX_PAGES = 50;

export type GridFetchOptions = {
  chunkSize?: number;
  maxPages?: number;
  /** Fetch multiple pages in parallel when total count is known (default 1 = sequential). */
  pageConcurrency?: number;
};

function getRecordId(row: any): string | null {
  const id = row?.id ?? row?._id ?? row?.pk;
  if (id === null || id === undefined || id === "") {
    return null;
  }
  return String(id);
}

function appendGridBatch(
  all: any[],
  batch: any[],
  seenIds: Set<string>
): number {
  let added = 0;
  for (const row of batch) {
    const key = getRecordId(row);
    if (key) {
      if (seenIds.has(key)) {
        continue;
      }
      seenIds.add(key);
    }
    all.push(row);
    added += 1;
  }
  return added;
}

async function fetchGridPagesWithConcurrency(
  fetchPage: (page: number) => Promise<any[]>,
  pages: number[],
  concurrency: number
): Promise<any[][]> {
  if (pages.length === 0) {
    return [];
  }

  const results: any[][] = new Array(pages.length);
  const queue = pages.map((page, index) => ({ page, index }));
  const workerCount = Math.min(Math.max(1, concurrency), queue.length);

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (queue.length > 0) {
        const entry = queue.shift();
        if (!entry) {
          break;
        }
        results[entry.index] = await fetchPage(entry.page);
      }
    })
  );

  return results;
}

function unwrapGridPayload(response: any): any {
  return response?.grid_data ?? response;
}

function extractGridRows(gridData: any): any[] {
  if (gridData?.results && Array.isArray(gridData.results)) {
    return gridData.results;
  }
  if (Array.isArray(gridData)) {
    return gridData;
  }
  if (
    gridData?.grid_data?.results &&
    Array.isArray(gridData.grid_data.results)
  ) {
    return gridData.grid_data.results;
  }
  return [];
}

function getGridTotalCount(gridData: any): number | undefined {
  const n =
    gridData?.count ??
    gridData?.total ??
    gridData?.total_count ??
    gridData?.grid_data?.count;
  if (typeof n === "number" && !Number.isNaN(n)) {
    return n;
  }
  return undefined;
}

function extractConfigRows(response: any): any[] {
  if (response?.results && Array.isArray(response.results)) {
    return response.results;
  }
  if (response?.data?.results && Array.isArray(response.data.results)) {
    return response.data.results;
  }
  return [];
}

class DynamicModuleService {
  getGridColItems = async (
    modelName: string,
    page: number,
    pageSize: number
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const response = await get(`${basePath}/${modelName}/grid_data/`, {
      params: {
        page,
        page_size: pageSize,
      },
    });
    return unwrapGridPayload(response);
  };

  /**
   * Fetches all grid rows by paging. When the first page includes a total count,
   * remaining pages are fetched in parallel (pageConcurrency) for large datasets.
   */
  getGridColItemsAllPages = async (
    modelName: string,
    options?: GridFetchOptions
  ): Promise<any[]> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const chunkSize = options?.chunkSize ?? GRID_FETCH_CHUNK;
    const maxPages = options?.maxPages ?? GRID_FETCH_MAX_PAGES;
    const pageConcurrency = options?.pageConcurrency ?? 1;

    const all: any[] = [];
    const seenIds = new Set<string>();

    const fetchPageBatch = async (page: number) => {
      const gridData = await this.getGridColItems(modelName, page, chunkSize);
      return {
        batch: extractGridRows(gridData),
        total: getGridTotalCount(gridData),
      };
    };

    const first = await fetchPageBatch(1);
    if (!first.batch.length) {
      return [];
    }

    appendGridBatch(all, first.batch, seenIds);

    if (first.total !== undefined && all.length >= first.total) {
      return all;
    }
    if (first.batch.length < chunkSize) {
      return all;
    }

    const knownTotalPages =
      first.total !== undefined
        ? Math.min(Math.ceil(first.total / chunkSize), maxPages)
        : undefined;

    if (
      knownTotalPages !== undefined &&
      knownTotalPages > 1 &&
      pageConcurrency > 1
    ) {
      const pageNumbers = Array.from(
        { length: knownTotalPages - 1 },
        (_, index) => index + 2
      );
      const batches = await fetchGridPagesWithConcurrency(
        async page => (await fetchPageBatch(page)).batch,
        pageNumbers,
        pageConcurrency
      );

      for (const batch of batches) {
        if (!batch?.length) {
          continue;
        }
        const added = appendGridBatch(all, batch, seenIds);
        if (added === 0) {
          break;
        }
      }

      return all;
    }

    for (let page = 2; page <= maxPages; page += 1) {
      const { batch, total } = await fetchPageBatch(page);
      if (!batch.length) {
        break;
      }

      const added = appendGridBatch(all, batch, seenIds);
      if (added === 0) {
        break;
      }

      if (total !== undefined && all.length >= total) {
        break;
      }
      if (batch.length < chunkSize) {
        break;
      }
    }

    return all;
  };

  /** Total row count from grid metadata when available; otherwise loads all pages. */
  getGridItemCount = async (modelName: string): Promise<number> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const gridData = await this.getGridColItems(modelName, 1, 1);
    const total = getGridTotalCount(gridData);
    if (total !== undefined) {
      return total;
    }
    const rows = await this.getGridColItemsAllPages(modelName);
    return rows.length;
  };

  getGridCoByConfigAllPages = async (
    modelName: string,
    columns: string[],
    paramsFilter: any,
    options?: GridFetchOptions
  ): Promise<any[]> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const chunkSize = options?.chunkSize ?? GRID_FETCH_CHUNK;
    const maxPages = options?.maxPages ?? GRID_FETCH_MAX_PAGES;
    const all: any[] = [];
    const seenIds = new Set<string>();
    // Server may cap `limit` below chunkSize, so advance by rows actually received
    let offset = 0;

    for (let page = 1; page <= maxPages; page += 1) {
      // Send both pagination styles: the list endpoint may page by page/page_size
      // (like grid_data) and ignore offset/limit, returning its default 20 rows.
      let response: any;
      try {
        response = await get(`${basePath}/${modelName}/`, {
          params: {
            offset,
            limit: chunkSize,
            page,
            page_size: chunkSize,
            columns: JSON.stringify(columns),
            ...paramsFilter,
          },
          silent: page > 1,
        });
      } catch (error) {
        // Page-number pagination 404s past the last page; keep what we have
        if (page > 1) {
          break;
        }
        throw error;
      }
      const batch = extractConfigRows(response);
      const total = getGridTotalCount(response);

      if (!batch.length) {
        break;
      }
      offset += batch.length;

      let added = 0;
      for (const row of batch) {
        const key = getRecordId(row);
        if (key) {
          if (seenIds.has(key)) {
            continue;
          }
          seenIds.add(key);
        }
        all.push(row);
        added += 1;
      }

      if (added === 0) {
        break;
      }

      if (total !== undefined && all.length >= total) {
        break;
      }
    }
    return all;
  };

  getGridCoByConfig = async (
    modelName: string,
    page: number,
    pageSize: number,
    columns: string[],
    paramsFilter: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const response = await get(`${basePath}/${modelName}/`, {
      params: {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        columns: JSON.stringify(columns),
        ...paramsFilter,
      },
    });
    return response;
  };

  getItemById = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await get(`${basePath}/${modelName}/${id}/`);
  };

  createItem = async (
    modelName: string,
    payload: DynamicPayload,
    options?: { silent?: boolean }
  ): Promise<any> => {
    if (!modelName || !payload) {
      throwError("Model name and payload are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${modelName}/`, payload, {
      silent: options?.silent,
    });
  };

  updateItem = async (
    modelName: string,
    id: string,
    payload: DynamicPayload,
    options?: { silent?: boolean }
  ): Promise<any> => {
    if (!modelName || !id || !payload) {
      throwError(
        "Model name, ID, and payload are required",
        "DynamicModuleService"
      );
    }
    return await put(`${basePath}/${modelName}/${id}/`, payload, {
      silent: options?.silent,
    });
  };

  deleteItem = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await del(`${basePath}/${modelName}/${id}/`);
  };

  bulkDeleteItems = async (modelName: string, ids: string[]): Promise<any> => {
    if (!modelName || !ids) {
      throwError("Model name and IDs are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${modelName}/bulk-delete/`, { ids });
  };

  getDropDownData = async (modelName: string): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    return await get(`${basePath}/${modelName}/drop_down_data/`);
  };
}

const instance = new DynamicModuleService();
export default instance;
